import React, { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowUpRight, Lock, ShieldCheck, Mail, Loader2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { HOME } from "@/constants/testIds";
import { BRAND, RESERVE_INCLUDES } from "@/constants/content";
import { saveLead, updateLead } from "@/services/leadService";
import { openRazorpayCheckout, RAZORPAY_CONFIGURED } from "@/services/paymentService";
import { sendReservationEmail, EMAIL_CONFIGURED } from "@/services/emailService";
import { useNavigate } from "react-router-dom";

const ReserveModal = ({ open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const canSubmit = name.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && phone.replace(/\D/g, "").length >= 8;

  const handleConfirm = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      const lead = await saveLead({ name: name.trim(), email: email.trim(), phone: phone.trim(), amountInr: BRAND.price });

      const goThankYou = async (payment) => {
        await updateLead(lead.id, { status: payment ? "paid" : "pending", ...(payment ? { paymentId: payment.paymentId } : {}) });
        if (EMAIL_CONFIGURED()) {
          await sendReservationEmail({ name: lead.name, email: lead.email, phone: lead.phone, paymentId: payment?.paymentId, amountInr: BRAND.price });
        }
        onOpenChange(false);
        navigate(`/thank-you?name=${encodeURIComponent(lead.name)}${payment?.paymentId ? `&pid=${encodeURIComponent(payment.paymentId)}` : ""}`);
      };

      if (!RAZORPAY_CONFIGURED()) {
        toast.success("Seat reserved!", { description: "Payments are not yet configured. We've saved your details." });
        await goThankYou(null);
        return;
      }

      // Close your modal before opening Razorpay
onOpenChange(false);

await openRazorpayCheckout({
  amountInr: BRAND.price,
  lead,

  onSuccess: async (payment) => {
    toast.success("Payment successful!", {
      description: `Payment ID: ${payment.paymentId}`,
    });
    await goThankYou(payment);
  },

  onDismiss: () => {
    toast("Checkout closed", {
      description: "You can continue your payment anytime.",
    });

    setLoading(false);
    onOpenChange(true);
  },

  onError: (err) => {
    toast.error("Payment failed", {
      description: err?.description || err?.message || "Please try again.",
    });

    setLoading(false);
    onOpenChange(true);
  },
});
    } catch (e) {
      toast.error("Something went wrong", { description: e?.message || "Please retry." });
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={HOME.reserveModal}
        className="max-w-lg p-0 border-0 overflow-hidden bg-transparent shadow-none"
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="relative rounded-3xl overflow-hidden glass-light shadow-premium border border-white/70"
        >
          <div className="aurora opacity-70" aria-hidden />
          <div className="relative z-10 p-6 sm:p-8">
            <DialogHeader>
              <div className="chip mb-3"><Lock className="w-3 h-3" /> Reserve your seat</div>
              <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900">
                Just one small step
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-2">
                We use these details to send your payment confirmation, official WhatsApp Community link, and all marathon updates.              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 grid gap-3">
              <div>
                <Label htmlFor="rm-name" className="text-xs uppercase tracking-widest font-mono text-slate-500">Full Name</Label>
                <Input id="rm-name" data-testid={HOME.reserveModalName} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1 h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="rm-email" className="text-xs uppercase tracking-widest font-mono text-slate-500">Email</Label>
                <Input id="rm-email" data-testid={HOME.reserveModalEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1 h-11 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="rm-phone" className="text-xs uppercase tracking-widest font-mono text-slate-500">WhatsApp Number</Label>
                <Input id="rm-phone" data-testid={HOME.reserveModalPhone} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" className="mt-1 h-11 rounded-xl" />
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-emerald-500/20 bg-emerald-50/50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-emerald-800">Order Summary</div>
                  <div className="text-sm font-semibold text-slate-900">BoostF eCommerce Launch Marathon</div>
                </div>
                <div className="flex items-baseline">
                  <IndianRupee className="w-4 h-4 text-slate-900" />
                  <div className="font-display font-bold text-2xl text-slate-900">{BRAND.price}</div>
                </div>
              </div>
              <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-emerald-900/80">
                {RESERVE_INCLUDES.slice(0, 6).map((i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-emerald-600" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={handleConfirm}
              data-testid={HOME.reserveModalConfirm}
              disabled={!canSubmit || loading}
              className="btn-primary w-full justify-center h-12 mt-5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {loading ? "Opening secure checkout…" : `Confirm & Pay ₹${BRAND.price}`}
              {!loading && <ArrowUpRight className="w-4 h-4" />}
            </button>

            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> 256-bit encrypted</span>
              <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-emerald-600" /> Instant confirmation</span>
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default ReserveModal;
